import React, { useState } from "react";
import { Headphones, Play, Pause, Waves, Wind, Gauge, ArrowLeft, Building2, BookMarked, Sparkles, Radio, Loader2, Tornado, Snowflake, Sunrise, Sun, Sunset, Moon, Clock, Footprints, Compass, Volume2, VolumeX, Music, Mic, Skull, Route, Cloud, Activity, Thermometer, Menu, X, Crosshair, Save, Trash2, SlidersHorizontal, Eye } from "lucide-react";
import TabSeal from "./TabSeal";
import { handleColor, readoutsFor, NODES, CITIES } from "../lib/storm";

function Panel({ children, className = "", ...rest }) {
  return <div className={`tab-panel ${className}`} {...rest}>{children}</div>;
}

const CYCLE_META = [
  { id: "hurricane", label: "Hurricane", Icon: Tornado },
  { id: "noreaster", label: "Nor'easter", Icon: Wind },
  { id: "blizzard", label: "Blizzard", Icon: Snowflake },
];
const TIME_META = [
  { id: "morning", label: "Morning", Icon: Sunrise },
  { id: "afternoon", label: "Afternoon", Icon: Sun },
  { id: "evening", label: "Evening", Icon: Sunset },
  { id: "night", label: "Night", Icon: Moon },
  { id: "auto", label: "Auto", Icon: Clock },
];
const VIEW_META = [
  { id: "realistic", label: "Visible", Icon: Cloud },
  { id: "forensic", label: "Forensic", Icon: Activity },
  { id: "thermal", label: "Thermal", Icon: Thermometer },
];
const CAT_TICKS = ["TS", "C1", "C2", "C3", "C4", "C5"];

export default function Hud({
  view, intensity, setIntensity, timeline, setTimeline, autoMode, setAutoMode,
  cycle, setCycle, timeOfDay, setTimeOfDay, autoSolar, cityCam, setCityCam,
  activeNode, liveMetrics, blizzardMetrics, events, selectedEvent, onSelectEvent,
  onDiveCity, selectedCity, onReturnGlobe, onPlayNarrator, narrator,
  onChase, chasePlaying, autoFollow, setAutoFollow,
  scenarios = [], onSaveScenario, onLoadScenario, onDeleteScenario,
  audioUI, setAudioUI, a11y, moveRef, xrStore, xrSupported = false, viewMode, setViewMode,
}) {
  const r = readoutsFor(intensity);
  const surgeM = (((intensity) / 5) * (selectedEvent && selectedEvent.surge_ft ? selectedEvent.surge_ft : 18) * 0.3048).toFixed(1);
  const visibility = Math.max(2, Math.round(100 - (intensity / 5) * 92));
  const tendency = intensity >= 4.6 ? "Bombogenesis" : intensity >= 3.2 ? "Falling Fast" : intensity >= 1.6 ? "Falling" : "Steady";
  const [mixer, setMixer] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const [menusOpen, setMenusOpen] = useState(false);
  const [scenName, setScenName] = useState("");
  const [archiveQ, setArchiveQ] = useState("");
  const archiveList = events.filter((e) => !archiveQ || `${e.name} ${e.type} ${e.year}`.toLowerCase().includes(archiveQ.toLowerCase()));
  const isCity = view === "city";
  const enterVR = async () => {
    try {
      const r = xrStore?.enterVR();
      if (r && typeof r.then === "function") await r;
    } catch (e) {
      console.error("enterVR failed", e);
      alert("Couldn't start the VR session. Open this page in a WebXR browser on Meta Quest 3 or Apple Vision Pro, then tap Enter VR.");
    }
  };
  const effMute = audioUI.mute || a11y.mute;
  const setMove = (key, val) => { if (moveRef?.current) moveRef.current[key] = val; };
  const dpadBtn = (key, label, testid) => (
    <button className="dpad-btn" data-testid={testid}
      onPointerDown={() => setMove(key, true)} onPointerUp={() => setMove(key, false)} onPointerLeave={() => setMove(key, false)}
    >{label}</button>
  );

  const IntensityBlock = ({ testid }) => (
    <div className="intensity-col">
      <div className="auto-row">
        <span className="ctrl-label">Storm Intensity · {r.label}</span>
        {!isCity && <button className={`auto-toggle ${autoMode ? "on" : ""}`} data-testid="auto-mode-toggle" onClick={() => setAutoMode(!autoMode)}>AUTO {autoMode ? "ON" : "OFF"}</button>}
      </div>
      <input className="h-slider mini" style={{ "--handle": handleColor(intensity) }} type="range" min="0" max="5" step="0.01"
        value={intensity} disabled={!isCity && autoMode} onChange={(e) => setIntensity(parseFloat(e.target.value))} data-testid={testid} />
      <div className="cat-ticks">{CAT_TICKS.map((t) => <span key={t}>{t}</span>)}</div>
    </div>
  );

  return (
    <div className="hud-root">
      {/* ===== Top-center branding seal ===== */}
      <div className="hud-seal" data-testid="hud-seal"><TabSeal size={52} /></div>

      {/* ===== Top-left icon cluster ===== */}
      <div className="hud-corner-tl">
        <button className="icon-btn" data-testid="report-menu-btn" onClick={() => setDrawer((d) => !d)} title="Report Menu"><Menu size={18} /></button>
        <button className={`icon-btn ${effMute ? "muted" : ""}`} data-testid="audio-btn" data-muted={effMute} onClick={() => setAudioUI((s) => ({ ...s, mute: !s.mute }))} title={effMute ? "Unmute storm" : "Mute storm"}>{effMute ? <VolumeX size={16} /> : <Volume2 size={16} />}</button>
        <button className="icon-btn" data-testid="audio-mixer-btn" onClick={() => setMixer((m) => !m)} title="Audio Mixer"><SlidersHorizontal size={16} /></button>
        <button className={`icon-btn ${xrSupported ? "xr-ready" : ""}`} data-testid="enter-vr-btn" data-xr-supported={xrSupported} onClick={enterVR} title={xrSupported ? "Enter VR (Quest 3 / Vision Pro)" : "Enter VR — open on a WebXR headset browser"}><Sparkles size={16} /></button>
        {isCity && <button className="icon-btn solid" data-testid="return-globe-btn" onClick={() => { setDrawer(false); onReturnGlobe(); }} title="Return to Space"><ArrowLeft size={16} /></button>}

        {mixer && (
          <div className="mixer" data-testid="audio-mixer" onClick={(e) => e.stopPropagation()}>
            <div className="panel-h"><Music size={14} /> Universal Mixer</div>
            <button className={`auto-toggle wide ${effMute ? "on" : ""}`} data-testid="mixer-mute" onClick={() => setAudioUI((s) => ({ ...s, mute: !s.mute }))}>{effMute ? "Muted" : "Master Mute"}</button>
            <div className="mix-row"><span><Waves size={13} /> Environment Chaos</span>
              <input type="range" min="0" max="1" step="0.01" value={audioUI.chaos} data-testid="mixer-chaos" onChange={(e) => setAudioUI((s) => ({ ...s, chaos: parseFloat(e.target.value) }))} /></div>
            <div className="mix-row"><span><Mic size={13} /> Narrative Voice</span>
              <input type="range" min="0" max="1" step="0.01" value={audioUI.voice} data-testid="mixer-voice" onChange={(e) => setAudioUI((s) => ({ ...s, voice: parseFloat(e.target.value) }))} /></div>
            <p className="mix-note">Cat 5 unleashes a bass-heavy roar; Level 0 rests on a calm space hum.</p>
          </div>
        )}
      </div>

      {/* ===== Slide-out Report drawer ===== */}
      <div className={`drawer ${drawer ? "open" : ""}`} data-testid="report-drawer">
        <div className="drawer-h">
          <span>{isCity ? `Micro-Witness · ${selectedCity?.name || ""}` : "The Report"}</span>
          <button className="a11y-x" onClick={() => setDrawer(false)} data-testid="drawer-close"><X size={18} /></button>
        </div>
        <div className="drawer-body">
          {!isCity ? (
            <>
              <div className="report-intro">
                <div className="brand-kicker">The Atmospheric Bureau · 25<sup>th</sup> Anniversary</div>
                <h1 className="brand-title">The Anatomy of a Hurricane, Nor'easter &amp; Blizzard</h1>
                <div className="brand-sub">A Spatial Computing Report · WebXR</div>
              </div>

              <Panel data-testid="node-panel">
                <div className="panel-h"><Radio size={14} /> Atmospheric Nodes</div>
                {Object.values(NODES).map((n) => (
                  <button key={n.id} className={`list-btn ${activeNode === n.id ? "active" : ""}`} data-testid={`node-btn-${n.id}`}
                    onClick={() => setCycle(n.id === "hurricane_polo" ? "hurricane" : "noreaster")}>
                    <span className={`dot ${n.kind}`} />
                    <span><strong>{n.name}</strong><em>{n.subtitle}</em></span>
                  </button>
                ))}
              </Panel>

              <Panel data-testid="city-panel">
                <div className="panel-h"><Building2 size={14} /> City Anchors — Dive In</div>
                <div className="chip-row">
                  {CITIES.map((c) => (
                    <button key={c.id} className="chip" data-testid={`dive-city-${c.id}`} onClick={() => onDiveCity(c)}>{c.name}</button>
                  ))}
                </div>
              </Panel>

              <Panel data-testid="archive-panel" className="archive">
                <div className="panel-h"><BookMarked size={14} /> Master Archive · {events.length} Storms</div>
                <input className="scen-input archive-search" data-testid="archive-search" value={archiveQ} placeholder="Search storms by name, type or year…" onChange={(e) => setArchiveQ(e.target.value)} />
                <div className="archive-scroll" data-testid="archive-scroll">
                  {archiveList.map((ev) => (
                    <button key={ev.id} className={`event-btn ${selectedEvent?.id === ev.id ? "active" : ""}`} data-testid={`event-btn-${ev.id}`} onClick={() => onSelectEvent(ev)}>
                      <strong>{ev.name}</strong><span>{ev.year} · {ev.type} · Peak {ev.peak_wind_mph} mph</span>
                    </button>
                  ))}
                  {archiveList.length === 0 && <div className="fs-empty" data-testid="archive-empty">No storms match “{archiveQ}”.</div>}
                </div>
                {selectedEvent && (
                  <div className="data-card" data-testid="tab-data-card">
                    <div className="card-h">TAB SITUATION REPORT</div>
                    <div className="card-title">{selectedEvent.name} ({selectedEvent.year})</div>
                    <p className="card-body report">{selectedEvent.report || selectedEvent.summary}</p>
                    <div className="card-stats">
                      <span><Wind size={11} /> {selectedEvent.peak_wind_mph} mph</span>
                      <span><Gauge size={11} /> {selectedEvent.peak_pressure_mb} mb</span>
                      {selectedEvent.snowfall_in > 0
                        ? <span><Snowflake size={11} /> {selectedEvent.snowfall_in}" snow</span>
                        : <span><Waves size={11} /> {selectedEvent.surge_ft} ft surge</span>}
                      <span className="toll" data-testid="death-toll"><Skull size={11} /> {selectedEvent.death_toll?.toLocaleString()} lost</span>
                    </div>
                    <div className="card-cite">
                      <span className="cite-h">Sources</span>
                      {selectedEvent.citations.map((c, i) => (<em key={i}>{c}</em>))}
                    </div>
                  </div>
                )}
              </Panel>

              <Panel data-testid="scenario-panel">
                <div className="panel-h"><Save size={14} /> Saved Scenarios</div>
                <div className="save-row">
                  <input className="scen-input" data-testid="scenario-name-input" value={scenName} placeholder="Name this storm scenario…"
                    onChange={(e) => setScenName(e.target.value)} />
                  <button className="gold-btn sm" data-testid="scenario-save-btn" onClick={() => { onSaveScenario && onSaveScenario(scenName.trim()); setScenName(""); }}>Save</button>
                </div>
                {scenarios.length === 0 && <p className="mix-note">Save the current system, intensity, time & view as a named scenario to reload anytime.</p>}
                {scenarios.map((s) => (
                  <div key={s.id} className="scenario-item" data-testid={`scenario-${s.id}`}>
                    <button className="scen-load" data-testid={`scenario-load-${s.id}`} onClick={() => onLoadScenario && onLoadScenario(s)}>
                      <strong>{s.name}</strong><span>{s.cycle} · L{Math.round(s.intensity)} · {s.view_mode}</span>
                    </button>
                    <button className="scen-del" data-testid={`scenario-del-${s.id}`} onClick={() => onDeleteScenario && onDeleteScenario(s.id)}><Trash2 size={13} /></button>
                  </div>
                ))}
              </Panel>
            </>
          ) : (
            <>
              <Panel data-testid="micro-witness-panel">
                <div className="panel-h"><Building2 size={14} /> Micro-Witness · {selectedCity?.name}</div>
                {selectedEvent ? (
                  <>
                    <div className="mw-event">{selectedEvent.name} ({selectedEvent.year})</div>
                    <div className="card-stats"><span><Waves size={11} /> {selectedEvent.surge_ft} ft surge</span><span><Wind size={11} /> {selectedEvent.peak_wind_mph} mph</span></div>
                    <p className="card-body">{selectedEvent.damage}</p>
                  </>
                ) : (
                  <p className="card-body">Move the Intensity slider to flood the streets. Level 5 unleashes debris, storm surge and near-horizontal rain.</p>
                )}
                <div className="card-stats">
                  <span><Waves size={11} /> Surge {(intensity / 5 * (selectedEvent ? selectedEvent.surge_ft : 18)).toFixed(1)} ft</span>
                  <span><Wind size={11} /> {readoutsFor(intensity).wind} mph</span>
                </div>
              </Panel>

              <Panel data-testid="narrator-panel">
                <div className="panel-h"><Headphones size={14} /> AI Situation Report</div>
                <button className="gold-btn wide" data-testid="narrator-play-btn" onClick={onPlayNarrator} disabled={narrator.loading}>
                  {narrator.loading ? <Loader2 size={15} className="spin" /> : <Play size={15} />}
                  {narrator.loading ? "Generating…" : narrator.playing ? "Playing…" : "Play Situation Report"}
                </button>
                {narrator.text && <p className="narrator-text" data-testid="narrator-text">{narrator.text}</p>}
              </Panel>
            </>
          )}
        </div>
      </div>

      {/* ===== Top-left stacked menus: System Selector + View Mode + Time + Auto-Follow ===== */}
      {!isCity && (
        <div className="hud-menu-stack" data-testid="menu-stack">
          <button className="menu-collapse-btn" data-testid="menu-collapse-btn" onClick={() => setMenusOpen((o) => !o)}><Menu size={15} /> Menu</button>
          <div className={`menu-groups ${menusOpen ? "open" : ""}`}>
            <div className="segmented sm" data-testid="system-selector">
              {CYCLE_META.map(({ id, label, Icon }) => (
                <button key={id} className={`seg-btn ${cycle === id ? "active" : ""}`} data-testid={`cycle-btn-${id}`} onClick={() => setCycle(id)}><Icon size={13} /> {label}</button>
              ))}
            </div>
            <div className="segmented sm" data-testid="view-toggle">
              {VIEW_META.map(({ id, label, Icon }) => (
                <button key={id} className={`seg-btn ${viewMode === id ? "active" : ""}`} data-testid={`view-${id}`} onClick={() => setViewMode(id)}><Icon size={12} /> {label}</button>
              ))}
            </div>
            <div className="segmented sm time" data-testid="time-menu">
              {TIME_META.map(({ id, label, Icon }) => (
                <button key={id} className={`seg-btn ${timeOfDay === id ? "active" : ""}`} data-testid={`time-btn-${id}`} onClick={() => setTimeOfDay(id)}><Icon size={12} /> {label}</button>
              ))}
            </div>
            <div className="segmented sm" data-testid="follow-toggle">
              <button className={`seg-btn ${autoFollow ? "active" : ""}`} data-testid="auto-follow-toggle" onClick={() => setAutoFollow(!autoFollow)}><Crosshair size={12} /> Auto-Follow {autoFollow ? "On" : "Off"}</button>
            </div>
            {timeOfDay === "auto" && autoSolar && <div className="sky-note" data-testid="sky-note">{autoSolar.sky_note}</div>}
          </div>
        </div>
      )}

      {/* ===== Far-left vertical Simulation Timeline ===== */}
      {!isCity && (
        <div className="hud-timeline-left" data-testid="timeline-cluster">
          <div className="tl-track"><input className="v-slider tall" type="range" min="0" max="1" step="0.01" value={timeline} onChange={(e) => setTimeline(parseFloat(e.target.value))} data-testid="timeline-slider" /></div>
          <label>Timeline</label>
          <button className={`chase-btn ${chasePlaying ? "on" : ""}`} data-testid="chase-btn" onClick={onChase} title="Storm Chase Playback">{chasePlaying ? <Pause size={13} /> : <Route size={13} />}</button>
        </div>
      )}

      {/* ===== Bottom-center Storm Intensity ===== */}
      <div className="hud-intensity-bc">
        {isCity && (
          <div className="segmented sm cam" data-testid="cam-toggle">
            <button className={`seg-btn ${cityCam === "walk" ? "active" : ""}`} data-testid="cam-walk" onClick={() => setCityCam("walk")}><Footprints size={13} /> Walk</button>
            <button className={`seg-btn ${cityCam === "orbit" ? "active" : ""}`} data-testid="cam-orbit" onClick={() => setCityCam("orbit")}><Compass size={13} /> Orbit</button>
          </div>
        )}
        <Panel className="sim-controls bc" data-testid="controls-panel">
          <IntensityBlock testid={isCity ? "intensity-slider-city" : "intensity-slider"} />
        </Panel>
      </div>

      {/* ===== Top-right: WIND / PRESSURE / CATEGORY data panel (below the icon buttons) ===== */}
      {!isCity && (
        <div className="hud-data-tr" data-testid="data-panel">
          <Panel className="metrics" data-testid="data-readout">
            <div className="readouts">
              <div className="readout"><span className="ro-label"><Wind size={12} /> Wind</span><span className="ro-val" data-testid="readout-wind">{r.wind} <em>mph</em></span></div>
              <div className="readout"><span className="ro-label"><Gauge size={12} /> Pressure</span><span className="ro-val" data-testid="readout-pressure">{r.pressure} <em>mb</em></span></div>
              <div className="readout"><span className="ro-label">Category</span><span className="ro-val cat" style={{ color: r.color }} data-testid="readout-category">{r.label}</span></div>
            </div>
            {cycle === "blizzard" && blizzardMetrics ? (
              <div className="live-strip" data-testid="live-strip">
                <span className={`live-dot ${blizzardMetrics.source === "simulated" ? "sim" : ""}`} />
                LIVE · {blizzardMetrics.snowfall_rate_inhr}"/hr · chill {blizzardMetrics.wind_chill_f}°F
              </div>
            ) : liveMetrics && (
              <div className="live-strip" data-testid="live-strip">
                <span className={`live-dot ${liveMetrics.source === "simulated" ? "sim" : ""}`} />
                LIVE · {liveMetrics.sustained_wind_mph} mph / {liveMetrics.central_pressure_mb} mb
              </div>
            )}
          </Panel>
        </div>
      )}

      {/* ===== Bottom-right: Forensic Indicators ===== */}
      {!isCity && (
        <div className="hud-forensic-br">
          {viewMode === "thermal" && (
            <div className="thermal-legend" data-testid="thermal-legend">
              <div className="tl-h"><Thermometer size={12} /> Thermal Core</div>
              <div className="tl-bar" />
              <div className="tl-scale"><span>Cold</span><span>Warm</span></div>
            </div>
          )}
          <Panel className="forensic" data-testid="forensic-panel">
            <div className="panel-h"><Activity size={13} /> Forensic Indicators</div>
            <div className="forensic-row"><span><Waves size={12} /> Storm Surge</span><strong data-testid="surge-meter">{surgeM} m</strong></div>
            <div className="forensic-row">
              <span><Eye size={12} /> Visibility</span><strong data-testid="visibility-gauge">{visibility}%</strong>
              <div className="vis-bar"><i style={{ width: `${visibility}%` }} /></div>
            </div>
            <div className="forensic-row"><span><Gauge size={12} /> Pressure Tendency</span><strong className="tendency" data-testid="pressure-tendency">{tendency}</strong></div>
          </Panel>
        </div>
      )}

      {/* ===== City walk D-pad (bottom-right) ===== */}
      {isCity && cityCam === "walk" && (
        <div className="dpad" data-testid="dpad">
          <div className="dpad-grid">
            <span />{dpadBtn("f", "▲", "dpad-forward")}<span />
            {dpadBtn("l", "◄", "dpad-left")}{dpadBtn("b", "▼", "dpad-back")}{dpadBtn("r", "►", "dpad-right")}
          </div>
          <div className="dpad-turn">
            {dpadBtn("tl", "↺", "dpad-turn-left")}
            <span className="dpad-hint">WASD / arrows · Q E turn</span>
            {dpadBtn("tr", "↻", "dpad-turn-right")}
          </div>
        </div>
      )}
    </div>
  );
}
